/**
 * factfindImport.service.ts — Layer 3: Fact-Find Document Import Domain
 *
 * Upload a fact-find / compliance document, extract every field via the Python
 * schema-driven pipeline (deduped + connected to the client's existing graph),
 * review, then commit into the knowledge graph.
 *
 *   startFactFindImport()  -> POST multipart; returns a jobId
 *   getFactFindImportJob() -> poll; returns status + preview + extractions
 *   commitFactFindImport() -> write the (reviewed) extractions into the graph
 */

import { getRequest, postRequest, postFormData, getRawResponse } from './http.service';
import {
  URL_FACTFIND_IMPORT_EXTRACT,
  URL_FACTFIND_IMPORT_STATUS,
  URL_FACTFIND_IMPORT_COMMIT,
  URL_FACTFIND_IMPORT_SOURCE_FILE,
  URL_FACTFIND_IMPORT_SOURCES,
} from './url.service';

export interface FactFindImportEntity {
  instanceId: string;
  class: string;
  properties: Record<string, unknown>;
  isNew: boolean;
  mergesInto: { instanceId: string; properties: Record<string, unknown> } | null;
  sourceChunkIds: string[];
}

export interface FactFindImportSection {
  class: string;
  label: string;
  entities: FactFindImportEntity[];
}

export interface FactFindImportJob {
  job_id: string;
  status: 'pending' | 'processing' | 'complete' | 'failed' | string;
  error?: string | null;
  message?: string | null;
  doc_id?: string;
  meeting_id?: string;
  transcription_id?: string;
  entity_count?: number;
  preview?: FactFindImportSection[];
  /** Raw extraction dicts to feed back verbatim to commit (possibly edited). */
  extractions?: Record<string, unknown>[];
}

export interface StartImportResult {
  success: boolean;
  jobId?: string;
  docId: string;
  fileName: string;
  blobPath?: string;
  /** True when this exact file (same content + name) was already imported — no re-parse. */
  alreadyImported?: boolean;
  importedAt?: string;
}

export interface CommitImportResult {
  success: boolean;
  ok: boolean;
  instances_inserted: number;
  updates_synced: boolean;
}

/** Upload a fact-find document and start async extraction. */
export const startFactFindImport = (
  clientId: string,
  file: File,
): Promise<StartImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  return postFormData<StartImportResult>(URL_FACTFIND_IMPORT_EXTRACT(clientId), formData);
};

/** Poll an extraction job for its status + preview. */
export const getFactFindImportJob = (jobId: string): Promise<FactFindImportJob> =>
  getRequest<FactFindImportJob>(URL_FACTFIND_IMPORT_STATUS(jobId));

export interface ImportSourceListItem {
  id: string;
  fileName: string;
  contentType?: string;
  importedAt?: string;
  hasFile: boolean;
}

/** List all uploaded fact-find documents for a client (newest first). */
export const listImportSources = (clientId: string): Promise<ImportSourceListItem[]> =>
  getRequest<{ sources: ImportSourceListItem[] }>(URL_FACTFIND_IMPORT_SOURCES(clientId)).then(
    (r) => r.sources || [],
  );

/** Fetch the original imported document as a Blob (for preview / download). */
export const fetchImportSourceFileBlob = async (sourceId: string): Promise<Blob> => {
  const res = await getRawResponse(URL_FACTFIND_IMPORT_SOURCE_FILE(sourceId));
  if (!res.ok) throw new Error("The source document could not be loaded.");
  return res.blob();
};

/** Commit the advisor-reviewed extractions into the client's knowledge graph. */
export const commitFactFindImport = (
  clientId: string,
  payload: {
    meetingId: string;
    transcriptionId: string;
    extractions: Record<string, unknown>[];
  },
): Promise<CommitImportResult> =>
  postRequest<CommitImportResult>(URL_FACTFIND_IMPORT_COMMIT(clientId), payload);
